import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Preferences from '../preferences';
import usePreferencesRoute from '~/modules/hooks/use-preferences-route';
import SwitchUser from '../switch-user';
import Button from '../button';
import UserService from '../../../services/user-service';
import AuthService from '../../../services/auth-service';
import { useAuth, UserButton } from '@clerk/clerk-react';

import {
	AvatarActions,
	AvatarButtons,
	AvatarMenuLink,
	AvatarOverlay,
	Container,
	MenuContainer,
	PreferencesButton,
	UserEmail,
	UserName
} from './avatar-menu.css';

import { MenuLabel } from '../main-menu/main-menu.css';
import { useQueryClient } from '@tanstack/react-query';

function AvatarMenu(props) {
	const {
		session,
		onLogout,
		isMaximized = false
	} = props;

	const isGuest = UserService.isGuest(session);

	const { signOut: signOutClerk } = useAuth();
	const [showMenu, setShowMenu] = useState(false);
	const [loggingOut, setLoggingOut] = useState(false);
	// Open state lives in the URL, so a refresh keeps the panel — and the tab — where it was.
	const preferencesRoute = usePreferencesRoute();
	const [showSwitchUser, setShowSwitchUser] = useState(false);

	const menuRef = useRef(null);

	const openMenu = (e) => {
		e.preventDefault();
		setShowMenu(true);
	};

	const closeMenu = (e) => {
		if( !menuRef?.current.contains(e.target) ) {
			setShowMenu(false);
		}
	};

	useEffect(() => {
		if( showMenu ) {
			setTimeout(() => {
				document.addEventListener('click', closeMenu);
			});
		}
		else {
			document.removeEventListener('click', closeMenu);
		}

		return () => {
			document.removeEventListener('click', closeMenu);
		};
	}, [showMenu]);

	const logOut = () => {
		signOutClerk();
		setLoggingOut(true);
	};

	useEffect(() => {
		if( loggingOut ) {
			onLogout();
		}
	}, [loggingOut]);

	const handlePreferencesClick = () => {
		setShowMenu(false);
		preferencesRoute.open();
	};

	const handleSwitchUserClick = () => {
		setShowMenu(false);
		setShowSwitchUser(true);
	};
	const queryClient = useQueryClient();
	const handleSwitchBackClick = () => {
		queryClient.clear();
		AuthService.switchBack();
	};

	const userName = session.name || session.emailNormal;
	const avatarSrc = `https://api.dicebear.com/7.x/initials/svg?seed=${userName}`;
	const isAdmin = UserService.isAdmin(session);
	const isImpersonating = AuthService.isImpersonating();

	return (
		<Container data-cy="avatar-menu-btn">
			<AvatarMenuLink onClick={openMenu}>
				<img src={avatarSrc} />
				<MenuLabel>My Profile</MenuLabel>
			</AvatarMenuLink>

			<MenuContainer ref={menuRef} isActive={showMenu}>

				<UserName>{userName}</UserName>
				{session.name && <UserEmail>{session.emailNormal}</UserEmail>}

				<AvatarActions>
					<AvatarButtons>
						<UserButton />
						<Button
							onClick={logOut}
							disabled={loggingOut}
							loading={loggingOut}
							data-cy="logout-btn"
						>
							Log Out
						</Button>
						{ isImpersonating && (
							<Button
								variant="secondary"
								lighten
								onClick={handleSwitchBackClick}
								data-testid="avatar-menu-switch-back-btn"
							>
									Switch Back
							</Button>
						) }
						{ isAdmin && !isImpersonating && (
							<Button
								variant="secondary"
								lighten
								onClick={handleSwitchUserClick}
							>
									Switch User
							</Button>
						) }
					</AvatarButtons>
					{ !isGuest
						&& <PreferencesButton
							use="settings"
							color="white"
							onClick={handlePreferencesClick}
						/>
					}
				</AvatarActions>
			</MenuContainer>

			<Preferences
				show={preferencesRoute.isOpen}
				tab={preferencesRoute.tab}
				onTabSelected={preferencesRoute.selectTab}
				onClose={preferencesRoute.close}
			/>

			<SwitchUser
				show={showSwitchUser}
				onClose={() => setShowSwitchUser(false)}
				current={session}
			/>

			<AvatarOverlay isActive={showMenu} isMaximized={isMaximized} />
		</Container>
	);
}

AvatarMenu.propTypes = {
	session: PropTypes.object.isRequired,
	onLogout: PropTypes.func.isRequired,
	isMaximized: PropTypes.bool
};

export default AvatarMenu;
